import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bot, Play, Pause, Plus, Radar, AlertTriangle, UserPlus, Crown, MessageSquare } from 'lucide-react';
import { api, Button, Notice, timeAgo, rememberModelTiers } from './ui';
import TeamSetup from './TeamSetup';
import TeamChat, { takeLocalDraft } from './TeamChat';
import ChatHistory from './ChatHistory';
import Pipeline, { OpportunityDrawer } from './Pipeline';
import Approvals from './Approvals';
import Holds from './Holds';
import Activity from './Activity';
import Network from './Network';
import Deals from './Deals';
import Sandbox from './Sandbox';
import ApproachDialog from './ApproachDialog';
import UpgradeCard, { teamCreationLocked } from './PlanGate';

/**
 * Agent Team (admin).
 *
 * One team per brand, six agents:
 *   Scout             finds untapped affiliates that fit the brand
 *   Vetter            checks each one really is relevant
 *   Contact agent     first emails, follow-ups, next address after a bounce
 *   Negotiator        checks the affiliate's claims and plans each move
 *   Affiliate Manager writes the replies the Negotiator plans, closes
 *   Stop agent        holds a deal stalled on terms, then checks in (Holds.jsx)
 */

const TABS = [
  { key: 'network', label: 'Network' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'holds', label: 'On hold' },
  { key: 'deals', label: 'Deals' },
  { key: 'sandbox', label: 'Sandbox' },
  { key: 'setup', label: 'Setup' },
  { key: 'activity', label: 'Activity' }
];

// Tabs that belong to the account, not to a team: they work before any team exists.
const ACCOUNT_TABS = ['deals', 'sandbox'];

const AgentTeam = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [teamId, setTeamId] = useState(null);
  // The team-setup chats, like a chat app's history, and the one open: { key, id, state }.
  // The open chat is in the address (?chat=12, or ?chat=new), so a tab reopened after closing it lands back in it.
  const [chats, setChats] = useState([]);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [openChat, setOpenChat] = useState(null);
  const openRef = useRef(null); // the same, read in callbacks before a render catches up
  const chatKey = useRef(0);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [openOpp, setOpenOpp] = useState(null);
  const [approaching, setApproaching] = useState(false);
  const [askUpgrade, setAskUpgrade] = useState(false);
  // A new team is made by answering questions; the full form is there for
  // anyone who would rather fill it in.
  const [formInstead, setFormInstead] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const tab = params.get('tab') || 'network';
  const chatParam = params.get('chat');
  const creating = !!chatParam;

  // One change to the address at a time: `null` removes a key.
  const setQuery = (patch, opts) => setParams((p) => {
    const n = new URLSearchParams(p);
    Object.entries(patch).forEach(([k, v]) => (v == null ? n.delete(k) : n.set(k, String(v))));
    return n;
  }, opts);
  const setTab = (key) => setQuery({ tab: key });

  const loadTeams = useCallback(async () => {
    try {
      const d = await api('/teams');
      setTeams(d.teams || []);
      setTeamId((cur) => cur ?? d.teams?.[0]?.id ?? null);
      setTeamsLoaded(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadChats = useCallback(async () => {
    try {
      const d = await api('/setup-chats');
      let list = d.chats || [];
      // A chat an earlier version kept in this browser only: moved to the server, once.
      const local = takeLocalDraft();
      if (local) {
        const c = await api('/setup-chats', { method: 'POST', body: { title: local.form.name || 'New team', state: local } });
        list = [c.chat, ...list];
      }
      setChats(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setChatsLoaded(true);
    }
  }, []);

  useEffect(() => {
    // The tier of each role, so a run's chip can say "fast" or "strong"
    // without ever naming the model it ran on.
    api('/meta').then((d) => { setMeta(d.meta); rememberModelTiers(d.meta?.models?.roles); }).catch((err) => setError(err.message));
    loadTeams();
    loadChats();
  }, [loadTeams, loadChats]);

  const showChat = (id, state) => {
    chatKey.current += 1;
    openRef.current = { key: chatKey.current, id, state };
    setOpenChat(openRef.current);
  };
  const startChat = (state = null, opts) => {
    showChat(null, state);
    setQuery({ chat: 'new', tab: 'setup' }, opts);
  };
  const closeChat = (patch = {}) => setQuery({ chat: null, ...patch });

  // The address names a chat (a reload, a reopened tab, Back): open it.
  useEffect(() => {
    if (!chatParam) return;
    const o = openRef.current;
    if (chatParam === 'new') {
      if (!o || o.id) showChat(null, null);
      return;
    }
    const n = Number(chatParam);
    if (o?.id === n) return;
    let gone = false;
    api(`/setup-chats/${n}`)
      .then((d) => { if (!gone) showChat(d.chat.id, d.chat.state); })
      .catch(() => { if (!gone) startChat(null, { replace: true }); });
    return () => { gone = true; };
    // Only when the address changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatParam]);

  // No team yet: the page is the chat, opened on the last one left unfinished.
  useEffect(() => {
    if (!teamsLoaded || !chatsLoaded || teams.length || chatParam || ACCOUNT_TABS.includes(tab)) return;
    const unfinished = chats.find((c) => !c.team_id);
    if (unfinished) setQuery({ chat: unfinished.id }, { replace: true });
    else startChat(null, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsLoaded, chatsLoaded, teams.length, chatParam, tab]);

  const pickChat = (id) => setQuery({ chat: id, tab: 'setup' });

  // Every save: the list shows it at the top, and a new chat takes its id (the address follows).
  const chatSaved = (key, chat) => {
    setChats((list) => [chat, ...list.filter((c) => c.id !== chat.id)]);
    const o = openRef.current;
    if (!o || o.key !== key || o.id) return;
    // The ref first, so the address change below does not load the chat it already shows.
    openRef.current = { ...o, id: chat.id };
    setOpenChat(openRef.current);
    setQuery({ chat: chat.id }, { replace: true });
  };

  const deleteChat = async (chat) => {
    if (!window.confirm(`Delete the chat "${chat.title}"?${chat.team_id ? ' The team it made is kept.' : ''}`)) return;
    try {
      await api(`/setup-chats/${chat.id}`, { method: 'DELETE' });
      setChats((list) => list.filter((c) => c.id !== chat.id));
      if (openChat?.id === chat.id) startChat(null, { replace: true });
    } catch (err) {
      setError(err.message);
    }
  };
  useEffect(() => {
    const t = setInterval(loadTeams, 30000);
    return () => clearInterval(t);
  }, [loadTeams]);

  const team = teams.find((t) => t.id === teamId) || null;
  const pendingAll = teams.reduce((s, t) => s + (t.pending_approvals || 0), 0);

  const setStatus = async (status) => {
    setBusy('status');
    setNotice(null);
    try {
      await api(`/teams/${team.id}/status`, { method: 'POST', body: { status } });
      await loadTeams();
    } catch (err) {
      setNotice({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const scoutNow = async () => {
    setBusy('scout');
    setNotice(null);
    try {
      await api(`/teams/${team.id}/scout`, { method: 'POST' });
      setNotice({ tone: 'info', text: 'The Scout is searching. Watch it on the Network tab; new affiliates appear under "Up next" in a minute or two.' });
      setTimeout(() => setRefreshKey((k) => k + 1), 45000);
    } catch (err) {
      setNotice({ tone: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  if (!meta) {
    return <div className="p-6">{error ? <Notice tone="error">{error}</Notice> : 'Loading...'}</div>;
  }

  // Enterprise only creates teams. Everything else on the page stays open, so
  // an account on another plan can still read what the agents do, talk to the
  // deal desk and play the sandbox — it just never reaches the team form.
  const locked = teamCreationLocked(meta);
  const showSetupOnly = (creating || !teams.length) && !ACCOUNT_TABS.includes(tab);
  const counts = team?.counts || {};
  const upgrade = () => navigate('/pricing-plan');
  const newTeam = () => {
    if (locked) {
      setAskUpgrade(true);
      return;
    }
    startChat();
  };
  // A setup left halfway (a tab closed by mistake), offered again from the team pages.
  const unfinished = chats.find((c) => !c.team_id && c.progress > 0);

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Bot className="h-7 w-7 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Affiliate Agent Team</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">Finds, vets, emails and negotiates with iGaming affiliates for your brands.</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {teams.length > 0 && (
            <select className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              value={creating ? '' : teamId || ''} onChange={(e) => { closeChat(); setTeamId(Number(e.target.value)); }}>
              {creating && <option value="">New team</option>}
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.brand_name})</option>)}
            </select>
          )}
          <Button icon={locked ? Crown : Plus} onClick={newTeam}
            title={locked ? 'Creating a team is part of the Enterprise plan' : undefined}>New team</Button>
        </div>
      </div>

      {!(meta.claude_configured ?? meta.openai_configured) && <Notice tone="error">The AI key is not set on the server, so the agents cannot run.</Notice>}
      {!meta.semrush_configured && <Notice tone="warn">Traffic lookups are not set up on the server: the agents cannot look up the brand's own traffic. Affiliates are checked on MARS data either way.</Notice>}
      {error && <Notice tone="error">{error}</Notice>}
      {/* The permanent card below already says this when there is no team. */}
      {askUpgrade && locked && !showSetupOnly && (
        <UpgradeCard gate={meta.plan} onUpgrade={upgrade} onClose={() => setAskUpgrade(false)} />
      )}

      {showSetupOnly ? (
        <>
          {locked ? (
            <>
              <UpgradeCard gate={meta.plan} onUpgrade={upgrade} />
              <Notice tone="info">
                You can still see how the agents work:{' '}
                <button type="button" className="font-medium underline" onClick={() => closeChat({ tab: 'sandbox' })}>play an affiliate in the Sandbox</button>
                {' '}or{' '}
                <button type="button" className="font-medium underline" onClick={() => closeChat({ tab: 'deals' })}>write the deal they would negotiate</button>
                . Both are ready for the day you give a team a brand.
              </Notice>
            </>
          ) : (
            <>
              {formInstead ? (
                <>
                  <button type="button" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline dark:text-blue-300" onClick={() => setFormInstead(false)}>
                    <MessageSquare className="h-4 w-4" />Answer questions instead
                  </button>
                  <TeamSetup meta={meta} team={null} onSaved={(t) => { setTeamId(t.id); loadTeams(); closeChat({ tab: 'setup' }); }} onDeleted={() => {}} onOpenDeals={() => setTab('deals')} />
                </>
              ) : (
                // The whole page is the chat: its history on the left, the open chat beside it.
                <div className="flex h-[calc(100vh-10rem)] min-h-[32rem] gap-4">
                  <ChatHistory className="hidden w-64 flex-none md:flex" chats={chats} currentId={openChat?.id}
                    onNew={() => startChat()} onPick={pickChat} onDelete={deleteChat} />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {chats.length > 0 && (
                      <select className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm md:hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        aria-label="Your chats" value={openChat?.id || ''}
                        onChange={(e) => (e.target.value ? pickChat(Number(e.target.value)) : startChat())}>
                        <option value="">New chat</option>
                        {chats.map((c) => <option key={c.id} value={c.id}>{c.title} · {c.team_id ? 'team created' : `${c.progress}%`}</option>)}
                      </select>
                    )}
                    <div className="min-h-0 flex-1">
                      {openChat ? (
                        <TeamChat key={openChat.key} meta={meta} teams={teams}
                          chatId={openChat.id} initial={openChat.state}
                          onSaved={(c) => chatSaved(openChat.key, c)}
                          onNewChat={(state) => startChat(state)}
                          // The chat stays open after a team is made, to set up the next brand from it.
                          onCreated={(t) => { setTeamId(t.id); loadTeams(); }}
                          onOpenTeam={(t) => { setTeamId(t.id); closeChat({ tab: 'network' }); }}
                          onUseForm={() => setFormInstead(true)}
                          onUpgrade={upgrade} />
                      ) : (
                        <div className="flex h-full items-center justify-center rounded-2xl border border-gray-200 bg-white text-sm text-gray-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">Opening your chat...</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {team && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900 dark:text-slate-100">{team.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${team.status === 'active'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400'}`}>{team.status === 'active' ? 'Running' : 'Paused'}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-slate-400">
                  {team.brand_name} · {(team.target_geos || []).join(', ') || 'no markets'} · from {(team.senders || []).map((s) => s.email).join(', ') || 'no mailbox'} · {team.sent_24h} sent in 24h
                  {team.last_scouted_at ? ` · scouted ${timeAgo(team.last_scouted_at)}` : ''}
                </div>
              </div>
              <div className="flex gap-4 text-center text-xs text-gray-500 dark:text-slate-400">
                {[['Up next', ['scouted', 'ready', 'queued']], ['Contacted', ['contacted']], ['Talking', ['replied', 'negotiating', 'closing']], ['On hold', ['on_hold']], ['Needs you', ['needs_human', 'review']], ['Won', ['won']]].map(([label, keys]) => (
                  <div key={label}><div className="text-lg font-semibold text-gray-900 dark:text-slate-100">{keys.reduce((s, k) => s + (counts[k] || 0), 0)}</div>{label}</div>
                ))}
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button icon={UserPlus} onClick={() => setApproaching(true)} disabled={!(team.senders || []).length} title={(team.senders || []).length ? 'Hand one affiliate to the Contact agent' : 'Choose a mailbox in Setup first'}>Approach an affiliate</Button>
                <Button icon={Radar} busy={busy === 'scout'} onClick={scoutNow}>Scout now</Button>
                {team.status === 'active'
                  ? <Button icon={Pause} busy={busy === 'status'} onClick={() => setStatus('paused')}>Pause team</Button>
                  : locked
                    ? <Button variant="primary" icon={Crown} onClick={() => setAskUpgrade(true)} title="Starting a team is part of the Enterprise plan">Start team</Button>
                    : <Button variant="primary" icon={Play} busy={busy === 'status'} disabled={team.readiness?.length > 0} onClick={() => setStatus('active')} title={team.readiness?.join(' ')}>Start team</Button>}
              </div>
            </div>
          )}
          {team?.readiness?.length > 0 && !['setup', 'deals', 'sandbox'].includes(tab) && (
            <Notice tone="warn"><div className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />Finish the setup to start this team: {team.readiness.join(' ')}</div></Notice>
          )}
          {notice && <Notice tone={notice.tone}>{notice.text}</Notice>}
          {unfinished && !locked && (
            <Notice tone="info">
              <div className="flex flex-wrap items-center gap-2">
                <MessageSquare className="h-4 w-4 flex-none" />
                <span>You have a team setup in progress: <span className="font-medium">{unfinished.title}</span>, {unfinished.progress}% done.</span>
                <button type="button" className="font-medium underline" onClick={() => pickChat(unfinished.id)}>Continue the chat</button>
              </div>
            </Notice>
          )}

          <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-slate-800">
            {TABS.filter((t) => team || ACCOUNT_TABS.includes(t.key)).map((t) => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${tab === t.key
                  ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200'}`}>
                {t.label}
                {t.key === 'approvals' && pendingAll > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-xs text-white">{pendingAll}</span>}
                {t.key === 'holds' && counts.on_hold > 0 && <span className="ml-1.5 rounded-full bg-indigo-500 px-1.5 text-xs text-white">{counts.on_hold}</span>}
              </button>
            ))}
          </div>

          {tab === 'deals' && <Deals meta={meta} focusId={params.get('deal') ? Number(params.get('deal')) : null} onSaved={() => loadTeams()} />}
          {team && tab === 'network' && <Network teamId={team.id} onOpenOpportunity={setOpenOpp} onApproach={() => setApproaching(true)} />}
          {tab === 'sandbox' && <Sandbox meta={meta} teams={teams} teamId={team?.id} onTeamsChanged={loadTeams} />}
          {team && tab === 'pipeline' && <Pipeline team={team} onOpen={setOpenOpp} refreshKey={refreshKey} />}
          {team && tab === 'holds' && <Holds teamId={team.id} onOpen={setOpenOpp} onChanged={loadTeams} />}
          {team && tab === 'approvals' &&<Approvals teamId={team.id} onChanged={loadTeams} onOpen={(oppId, tId) => { if (tId) setTeamId(tId); setOpenOpp(oppId); }} />}
          {team && tab === 'setup' && <TeamSetup meta={meta} team={team} onSaved={() => loadTeams()} onDeleted={() => { setTeamId(null); loadTeams(); setTab('network'); }} onOpenDeals={() => setTab('deals')} />}
          {team && tab === 'activity' && <Activity team={team} onOpen={setOpenOpp} />}
        </>
      )}

      {approaching && team && (
        <ApproachDialog team={team} onClose={() => setApproaching(false)}
          onDone={(oppId) => { setApproaching(false); setOpenOpp(oppId); setRefreshKey((k) => k + 1); loadTeams(); }} />
      )}
      {openOpp && <OpportunityDrawer id={openOpp} onClose={() => setOpenOpp(null)} onChanged={() => { setRefreshKey((k) => k + 1); loadTeams(); }} />}
    </div>
  );
};

export default AgentTeam;
